/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
/**
 * Local roles a citizen account can hold inside a company.
 * `citizen` is the default: the backend stores it as NULL, so it is
 * normalized here to keep the UI from having to deal with empty values.
 */
export const CITIZEN_ROLE_DEFAULT = 'citizen'

export const CITIZEN_ROLES = [
    'citizen',
    'technical_agent',
    'neighborhood_committee'
]

export const CITIZEN_ROLE_VARIANTS = {
    citizen: 'neutral',
    technical_agent: 'info',
    neighborhood_committee: 'primary'
}

export const CITIZEN_ROLE_ICONS = {
    citizen: 'fal fa-user',
    technical_agent: 'fal fa-hard-hat',
    neighborhood_committee: 'fal fa-users'
}

/**
 * Reads a list of citizen roles off a payload, whatever shape it arrives in:
 * an array of slugs, an array of pivot objects, a JSON-encoded string (a column
 * without a cast) or a comma-separated string. Unknown roles are dropped.
 *
 * Returns an empty array when nothing usable is found — callers must treat that
 * as "the payload said nothing", not as "no role selected".
 */
export function parseCitizenRoles(value) {
    if (value === null || value === undefined) return []

    let list = value

    if (typeof list === 'string') {
        const trimmed = list.trim()
        if (!trimmed) return []
        try {
            list = JSON.parse(trimmed)
        } catch (e) {
            list = trimmed.split(',')
        }
    }

    if (!Array.isArray(list)) return []

    return list
        .map((item) => {
            if (typeof item === 'string') return item.trim()
            if (item && typeof item === 'object') {
                return item.role || item.name || item.slug || item.value || ''
            }
            return ''
        })
        .filter((role) => CITIZEN_ROLES.includes(role))
}

/**
 * Reads the local role off a citizen row, whatever shape the backend uses
 * (flat column, pivot row or nested citizen relation), and falls back to
 * the default role when it is null/unknown.
 */
export function normalizeCitizenRole(row) {
    if (!row) return CITIZEN_ROLE_DEFAULT

    const role = row.role
        || (row.pivot && row.pivot.role)
        || (row.citizen && row.citizen.role)

    return CITIZEN_ROLES.includes(role) ? role : CITIZEN_ROLE_DEFAULT
}
